import { c as _c } from "react/compiler-runtime";
// Раздел «Клиенты продавцов» консоли (встреча 17.10.2026).
//
// Продавец приводит своих клиентов по ссылке /s/<код> или QR. Новый для
// площадки покупатель закрепляется за магазином, и в льготный срок с его
// заказов у этого магазина удерживается только эквайринг (снимок комиссии
// заказа, источник «Клиент продавца»).
//
// Вкладки:
//   • «Привязки» - кто, к какому магазину, когда и по какой ссылке; снять или
//     перепривязать с причиной (в журнал действий);
//   • «Магазины» - сводка за период: привязано, заказов, сколько комиссии не
//     взято; персональный льготный срок магазина;
//   • «Настройки» - включено, срок по умолчанию, ставка эквайринга.
import type { SellerClientBinding, SellerClientShopSummary } from "@osmix/contracts";
import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { cn } from "@/lib/utils";
import { useAuth } from "@/store";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { type BindingFilter, dateOnly, dateTime, lastDays, monthsLabel, pct, rub, sellerClientsAvailable, useSellerClientBindings, useSellerClientMutations, useSellerClientSettings, useSellerClientShopOptions, useSellerClientSummary } from "./seller-clients-api";
type Tab = "bindings" | "shops" | "settings";
const TABS: {
  key: Tab;
  label: string;
}[] = [{
  key: "bindings",
  label: "Привязки"
}, {
  key: "shops",
  label: "Магазины"
}, {
  key: "settings",
  label: "Настройки"
}];
const MANAGE = "action.promos.manage";
export function SellerClientsPage() {
  const $ = _c(24);
  const [params, setParams] = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = params.get("tab") as Tab || "bindings";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tab = t0;
  let t1;
  if ($[2] !== params || $[3] !== setParams) {
    t1 = next => {
      const p = new URLSearchParams(params);
      if (next === "bindings") {
        p.delete("tab");
      } else {
        p.set("tab", next);
      }
      setParams(p, {
        replace: true
      });
    };
    $[2] = params;
    $[3] = setParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const setTab = t1;
  if (!sellerClientsAvailable) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p className="py-16 text-center text-sm text-muted-foreground">Раздел работает при подключении к шлюзу.</p>;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div><h1 className="text-xl font-semibold">Клиенты продавцов</h1><p className="text-sm text-muted-foreground">Покупатели, которых магазины привели сами по своей ссылке или QR. В льготный срок с их заказов у этого магазина не берётся комиссия OSMIX; эквайринг, логистика, возвраты и CPA-продвижение удерживаются как обычно. У других магазинов - обычная комиссия.</p></div>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== setTab || $[8] !== tab) {
    t3 = TABS.map(t => <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} data-testid={`sc-tab-${t.key}`} onClick={() => setTab(t.key)} className={cn("-mb-px shrink-0 border-b-2 px-4 py-2 text-sm", tab === t.key ? "border-primary font-medium text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{t.label}</button>);
    $[7] = setTab;
    $[8] = tab;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <div className="flex gap-1 overflow-x-auto border-b" role="tablist">{t3}</div>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== tab) {
    t5 = tab === "bindings" && <BindingsTab />;
    $[12] = tab;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== setParams || $[15] !== tab) {
    t6 = tab === "shops" && <ShopsTab onOpenShop={shopId => setParams(new URLSearchParams({
      shop: shopId
    }), {
      replace: true
    })} />;
    $[14] = setParams;
    $[15] = tab;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== tab) {
    t7 = tab === "settings" && <SettingsTab />;
    $[17] = tab;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className="flex flex-col gap-6">{t2}{t4}{t5}{t6}{t7}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}

// ── Привязки ───────────────────────────────────────────────────────────────

function GraceBadge(t0) {
  const $ = _c(7);
  const {
    b
  } = t0;
  if (b.status === "ended") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Снята</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = b.inGrace ? "bg-emerald-100 text-emerald-800" : "bg-muted text-foreground";
  let t2;
  if ($[1] !== t1) {
    t2 = cn("inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap", t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `${monthsLabel(b.graceMonths)} от привязки`;
  const t4 = b.inGrace ? `Льгота до ${dateOnly(b.graceEndsAt)}` : `Льгота закончилась ${dateOnly(b.graceEndsAt)}`;
  let t5;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = <span className={t2} title={t3}>{t4}</span>;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function BindingsTab() {
  const $ = _c(99);
  const [params] = useSearchParams();
  const t0 = useAuth(_temp);
  let t1;
  if ($[0] !== t0) {
    t1 = hasAdminPermission(t0, MANAGE);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canManage = t1;
  let t2;
  if ($[2] !== params) {
    t2 = params.get("shop") ?? "";
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      status: "active",
      grace: "all",
      shopId: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [filter, setFilter] = useState(t3);
  const [q, setQ] = useState("");
  let t4;
  let t5;
  if ($[6] !== q) {
    t4 = () => {
      const id = setTimeout(() => setFilter(f => ({
        ...f,
        q: q.trim()
      })), 300);
      return () => clearTimeout(id);
    };
    t5 = [q];
    $[6] = q;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const shops = useSellerClientShopOptions();
  const {
    data,
    isLoading,
    error
  } = useSellerClientBindings(filter);
  let t6;
  if ($[9] !== data) {
    t6 = data ?? [];
    $[9] = data;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const list = t6;
  const [unbinding, setUnbinding] = useState(null);
  const [rebinding, setRebinding] = useState(null);
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e => setQ(e.target.value);
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== q) {
    t9 = <div className="relative w-full max-w-xs">{t7}<Input value={q} onChange={t8} placeholder={"\u0418\u043C\u044F, \u0442\u0435\u043B\u0435\u0444\u043E\u043D, \u043F\u043E\u0447\u0442\u0430, \u043A\u043E\u0434 \u0441\u0441\u044B\u043B\u043A\u0438"} className="pl-8" data-testid="sc-search" /></div>;
    $[13] = q;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = filter.shopId ?? "";
  let t11;
  if ($[15] !== filter) {
    t11 = e_0 => setFilter({
      ...filter,
      shopId: e_0.target.value
    });
    $[15] = filter;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <option value="">Все магазины</option>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== shops.data) {
    t13 = shops.data ?? [];
    $[18] = shops.data;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t13) {
    t14 = t13.map(_temp2);
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t14) {
    t15 = <Select value={t10} onChange={t11} aria-label={"\u041C\u0430\u0433\u0430\u0437\u0438\u043D"}>{t12}{t14}</Select>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== filter) {
    t16 = e_1 => setFilter({
      ...filter,
      status: e_1.target.value as BindingFilter["status"]
    });
    $[26] = filter;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  let t18;
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <option value="active">Действующие</option>;
    t18 = <option value="ended">Снятые</option>;
    t19 = <option value="all">Все</option>;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
  } else {
    t17 = $[28];
    t18 = $[29];
    t19 = $[30];
  }
  let t20;
  if ($[31] !== filter.status || $[32] !== t16) {
    t20 = <Select value={filter.status} onChange={t16} aria-label={"\u0421\u0442\u0430\u0442\u0443\u0441"}>{t17}{t18}{t19}</Select>;
    $[31] = filter.status;
    $[32] = t16;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== filter) {
    t21 = e_2 => setFilter({
      ...filter,
      grace: e_2.target.value as BindingFilter["grace"]
    });
    $[34] = filter;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  let t23;
  let t24;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <option value="all">Льгота: любая</option>;
    t23 = <option value="in">Льгота идёт</option>;
    t24 = <option value="out">Льгота закончилась</option>;
    $[36] = t22;
    $[37] = t23;
    $[38] = t24;
  } else {
    t22 = $[36];
    t23 = $[37];
    t24 = $[38];
  }
  let t25;
  if ($[39] !== filter.grace || $[40] !== t21) {
    t25 = <Select value={filter.grace} onChange={t21} aria-label={"\u041B\u044C\u0433\u043E\u0442\u0430"}>{t22}{t23}{t24}</Select>;
    $[39] = filter.grace;
    $[40] = t21;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  const t26 = filter.from ?? "";
  let t27;
  if ($[42] !== filter) {
    t27 = e_3 => setFilter({
      ...filter,
      from: e_3.target.value
    });
    $[42] = filter;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t26 || $[45] !== t27) {
    t28 = <label className="flex items-center gap-1 text-sm text-muted-foreground">с<Input type="date" value={t26} onChange={t27} className="w-[150px]" aria-label={"\u041F\u0440\u0438\u0432\u044F\u0437\u0430\u043D \u0441"} /></label>;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  const t29 = filter.to ?? "";
  let t30;
  if ($[47] !== filter) {
    t30 = e_4 => setFilter({
      ...filter,
      to: e_4.target.value
    });
    $[47] = filter;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] !== t29 || $[50] !== t30) {
    t31 = <label className="flex items-center gap-1 text-sm text-muted-foreground">по<Input type="date" value={t29} onChange={t30} className="w-[150px]" aria-label={"\u041F\u0440\u0438\u0432\u044F\u0437\u0430\u043D \u043F\u043E"} /></label>;
    $[49] = t29;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  let t32;
  if ($[52] !== t15 || $[53] !== t20 || $[54] !== t25 || $[55] !== t28 || $[56] !== t31 || $[57] !== t9) {
    t32 = <div className="flex flex-wrap items-center gap-2">{t9}{t15}{t20}{t25}{t28}{t31}</div>;
    $[52] = t15;
    $[53] = t20;
    $[54] = t25;
    $[55] = t28;
    $[56] = t31;
    $[57] = t9;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== error) {
    t33 = error && <p className="text-sm text-destructive">Не удалось загрузить привязки: {(error as Error).message}</p>;
    $[59] = error;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  let t35;
  let t36;
  let t37;
  let t38;
  let t39;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <TableHead>Покупатель</TableHead>;
    t35 = <TableHead>Магазин</TableHead>;
    t36 = <TableHead>Привязка</TableHead>;
    t37 = <TableHead>Льгота</TableHead>;
    t38 = <TableHead className="text-right">Заказы</TableHead>;
    t39 = <TableHead className="text-right">Не взято комиссии</TableHead>;
    $[61] = t34;
    $[62] = t35;
    $[63] = t36;
    $[64] = t37;
    $[65] = t38;
    $[66] = t39;
  } else {
    t34 = $[61];
    t35 = $[62];
    t36 = $[63];
    t37 = $[64];
    t38 = $[65];
    t39 = $[66];
  }
  let t40;
  if ($[67] !== canManage) {
    t40 = canManage && <TableHead />;
    $[67] = canManage;
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  let t41;
  if ($[69] !== t40) {
    t41 = <TableHeader><TableRow>{t34}{t35}{t36}{t37}{t38}{t39}{t40}</TableRow></TableHeader>;
    $[69] = t40;
    $[70] = t41;
  } else {
    t41 = $[70];
  }
  let t42;
  if ($[71] !== isLoading) {
    t42 = isLoading && <TableRow><TableCell colSpan={7} className="h-20 text-center text-muted-foreground"><Loader2 className="mx-auto size-4 animate-spin" /></TableCell></TableRow>;
    $[71] = isLoading;
    $[72] = t42;
  } else {
    t42 = $[72];
  }
  let t43;
  if ($[73] !== isLoading || $[74] !== list.length) {
    t43 = !isLoading && list.length === 0 && <TableRow><TableCell colSpan={7} className="h-20 text-center text-muted-foreground">Привязок нет.</TableCell></TableRow>;
    $[73] = isLoading;
    $[74] = list.length;
    $[75] = t43;
  } else {
    t43 = $[75];
  }
  let t44;
  if ($[76] !== canManage || $[77] !== list) {
    let t45;
    if ($[79] !== canManage) {
      t45 = b => <TableRow key={b.id} data-testid="sc-row"><TableCell><div className="font-medium">{b.buyerName || "\u0411\u0435\u0437 \u0438\u043C\u0435\u043D\u0438"}</div><div className="text-xs text-muted-foreground">{[b.buyerPhone, b.buyerEmail].filter(Boolean).join(" \xB7 ") || b.buyerUserId}</div></TableCell><TableCell className="max-w-[180px] truncate">{b.shopName}</TableCell><TableCell className="whitespace-nowrap">{dateTime(b.boundAt)}<div className="text-xs text-muted-foreground">{b.source === "link" ? `по ссылке ${b.code ?? ""}${b.visitedAt ? `, переход ${dateTime(b.visitedAt)}` : ""}` : `администратор${b.createdByName ? ` ${b.createdByName}` : ""}`}</div>{b.reason && <div className="max-w-[240px] truncate text-xs text-muted-foreground" title={b.reason}>{b.reason}</div>}{b.status === "ended" && <div className="max-w-[240px] truncate text-xs text-muted-foreground" title={b.endReason ?? ""}>снята {dateTime(b.endedAt)}{b.endedByName ? `, ${b.endedByName}` : ""}: {b.endReason}</div>}</TableCell><TableCell><GraceBadge b={b} /></TableCell><TableCell className="text-right tabular-nums whitespace-nowrap">{b.orders}<div className="text-xs text-muted-foreground">льготных {b.graceOrders}</div></TableCell><TableCell className="text-right tabular-nums whitespace-nowrap">{rub(b.savedCommission)}</TableCell>{canManage && <TableCell className="whitespace-nowrap text-right">{b.status === "active" && <div className="flex justify-end gap-1"><Button size="sm" variant="outline" onClick={() => setRebinding(b)} data-testid="sc-rebind">Сменить магазин</Button><Button size="sm" variant="ghost" onClick={() => setUnbinding(b)} data-testid="sc-unbind">Снять</Button></div>}</TableCell>}</TableRow>;
      $[79] = canManage;
      $[80] = t45;
    } else {
      t45 = $[80];
    }
    t44 = list.map(t45);
    $[76] = canManage;
    $[77] = list;
    $[78] = t44;
  } else {
    t44 = $[78];
  }
  let t45;
  if ($[81] !== t42 || $[82] !== t43 || $[83] !== t44) {
    t45 = <TableBody>{t42}{t43}{t44}</TableBody>;
    $[81] = t42;
    $[82] = t43;
    $[83] = t44;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== t41 || $[86] !== t45) {
    t46 = <div className="overflow-x-auto rounded-lg border"><Table>{t41}{t45}</Table></div>;
    $[85] = t41;
    $[86] = t45;
    $[87] = t46;
  } else {
    t46 = $[87];
  }
  let t47;
  if ($[88] !== unbinding) {
    t47 = unbinding && <UnbindDialog binding={unbinding} onClose={() => setUnbinding(null)} />;
    $[88] = unbinding;
    $[89] = t47;
  } else {
    t47 = $[89];
  }
  let t48;
  if ($[90] !== rebinding || $[91] !== shops.data) {
    t48 = rebinding && <RebindDialog binding={rebinding} shops={shops.data ?? []} onClose={() => setRebinding(null)} />;
    $[90] = rebinding;
    $[91] = shops.data;
    $[92] = t48;
  } else {
    t48 = $[92];
  }
  let t49;
  if ($[93] !== t32 || $[94] !== t33 || $[95] !== t46 || $[96] !== t47 || $[97] !== t48) {
    t49 = <div className="flex flex-col gap-3">{t32}{t33}{t46}{t47}{t48}</div>;
    $[93] = t32;
    $[94] = t33;
    $[95] = t46;
    $[96] = t47;
    $[97] = t48;
    $[98] = t49;
  } else {
    t49 = $[98];
  }
  return t49;
}
function _temp2(s_0) {
  return <option key={s_0.shopId} value={s_0.shopId}>{s_0.shopName}</option>;
}
function _temp(s) {
  return s.user;
}
function ReasonField(t0) {
  const $ = _c(5);
  const {
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== value) {
    t2 = <textarea value={value} onChange={t1} rows={3} maxLength={500} placeholder={"\u041F\u0440\u0438\u0447\u0438\u043D\u0430 - \u043F\u043E\u043F\u0430\u0434\u0451\u0442 \u0432 \u0436\u0443\u0440\u043D\u0430\u043B \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0439"} className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="sc-reason" />;
    $[2] = t1;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function UnbindDialog(t0) {
  const $ = _c(39);
  const {
    binding,
    onClose
  } = t0;
  const {
    unbind
  } = useSellerClientMutations();
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  let t1;
  if ($[0] !== binding.id || $[1] !== onClose || $[2] !== reason || $[3] !== unbind) {
    t1 = async () => {
      setError("");
      ;
      try {
        await unbind.mutateAsync({
          id: binding.id,
          reason: reason.trim()
        });
        onClose();
      } catch (t2) {
        const e = t2;
        setError((e as Error).message);
      }
    };
    $[0] = binding.id;
    $[1] = onClose;
    $[2] = reason;
    $[3] = unbind;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const submit = t1;
  let t2;
  if ($[5] !== onClose) {
    t2 = open => !open && onClose();
    $[5] = onClose;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogTitle>Снять привязку</DialogTitle>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = binding.buyerName || binding.buyerUserId;
  let t5;
  if ($[8] !== binding.shopName || $[9] !== t4) {
    t5 = <DialogHeader>{t3}<DialogDescription>{t4} перестанет быть клиентом магазина «{binding.shopName}». Новые заказы пойдут по обычной ставке, уже оформленные не меняются.</DialogDescription></DialogHeader>;
    $[8] = binding.shopName;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== reason) {
    t6 = <ReasonField value={reason} onChange={setReason} />;
    $[11] = reason;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== error) {
    t7 = error && <p className="text-sm text-destructive">{error}</p>;
    $[13] = error;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== onClose) {
    t8 = <Button variant="outline" onClick={onClose}>Отмена</Button>;
    $[15] = onClose;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== reason || $[18] !== unbind.isPending) {
    t9 = reason.trim().length < 3 || unbind.isPending;
    $[17] = reason;
    $[18] = unbind.isPending;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== submit) {
    t10 = () => void submit();
    $[20] = submit;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== unbind.isPending) {
    t11 = unbind.isPending && <Loader2 className="size-4 animate-spin" />;
    $[22] = unbind.isPending;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t9) {
    t12 = <Button variant="destructive" disabled={t9} onClick={t10}>{t11}Снять</Button>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t8) {
    t13 = <DialogFooter>{t8}{t12}</DialogFooter>;
    $[28] = t12;
    $[29] = t8;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7) {
    t14 = <DialogContent>{t5}{t6}{t7}{t13}</DialogContent>;
    $[31] = t13;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t14 || $[37] !== t2) {
    t15 = <Dialog open={true} onOpenChange={t2}>{t14}</Dialog>;
    $[36] = t14;
    $[37] = t2;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  return t15;
}
function RebindDialog(t0) {
  const $ = _c(51);
  const {
    binding,
    shops,
    onClose
  } = t0;
  const {
    bind
  } = useSellerClientMutations();
  const [shopId, setShopId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  let t1;
  if ($[0] !== bind || $[1] !== binding.buyerUserId || $[2] !== onClose || $[3] !== reason || $[4] !== shopId) {
    t1 = async () => {
      setError("");
      ;
      try {
        await bind.mutateAsync({
          buyerUserId: binding.buyerUserId,
          shopId,
          reason: reason.trim()
        });
        onClose();
      } catch (t2) {
        const e = t2;
        setError((e as Error).message);
      }
    };
    $[0] = bind;
    $[1] = binding.buyerUserId;
    $[2] = onClose;
    $[3] = reason;
    $[4] = shopId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const submit = t1;
  let t2;
  if ($[6] !== onClose) {
    t2 = open => !open && onClose();
    $[6] = onClose;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogTitle>Перепривязать к другому магазину</DialogTitle>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== binding.shopName) {
    t4 = <DialogHeader>{t3}<DialogDescription>Привязка к «{binding.shopName}» снимется, льготный срок у нового магазина начнётся с сегодняшнего дня.</DialogDescription></DialogHeader>;
    $[9] = binding.shopName;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => setShopId(e_0.target.value);
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <option value="">Выберите магазин</option>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== binding.shopId || $[14] !== shops) {
    let t8;
    if ($[16] !== binding.shopId) {
      t8 = s => s.shopId !== binding.shopId;
      $[16] = binding.shopId;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t7 = shops.filter(t8).map(_temp3);
    $[13] = binding.shopId;
    $[14] = shops;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[18] !== shopId || $[19] !== t7) {
    t8 = <Select value={shopId} onChange={t5} aria-label={"\u041D\u043E\u0432\u044B\u0439 \u043C\u0430\u0433\u0430\u0437\u0438\u043D"} className="w-full">{t6}{t7}</Select>;
    $[18] = shopId;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== reason) {
    t9 = <ReasonField value={reason} onChange={setReason} />;
    $[21] = reason;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== error) {
    t10 = error && <p className="text-sm text-destructive">{error}</p>;
    $[23] = error;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== onClose) {
    t11 = <Button variant="outline" onClick={onClose}>Отмена</Button>;
    $[25] = onClose;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== bind.isPending || $[28] !== reason || $[29] !== shopId) {
    t12 = !shopId || reason.trim().length < 3 || bind.isPending;
    $[27] = bind.isPending;
    $[28] = reason;
    $[29] = shopId;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== submit) {
    t13 = () => void submit();
    $[31] = submit;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== bind.isPending) {
    t14 = bind.isPending && <Loader2 className="size-4 animate-spin" />;
    $[33] = bind.isPending;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t12 || $[36] !== t13 || $[37] !== t14) {
    t15 = <Button disabled={t12} onClick={t13}>{t14}Перепривязать</Button>;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t11 || $[40] !== t15) {
    t16 = <DialogFooter>{t11}{t15}</DialogFooter>;
    $[39] = t11;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t10 || $[43] !== t16 || $[44] !== t4 || $[45] !== t8 || $[46] !== t9) {
    t17 = <DialogContent>{t4}{t8}{t9}{t10}{t16}</DialogContent>;
    $[42] = t10;
    $[43] = t16;
    $[44] = t4;
    $[45] = t8;
    $[46] = t9;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t17 || $[49] !== t2) {
    t18 = <Dialog open={true} onOpenChange={t2}>{t17}</Dialog>;
    $[48] = t17;
    $[49] = t2;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
}

// ── Магазины ───────────────────────────────────────────────────────────────
function _temp3(s_0) {
  return <option key={s_0.shopId} value={s_0.shopId}>{s_0.shopName}</option>;
}
const SUMMARY_DAYS = [7, 30, 90, 365] as const;
function ShopsTab(t0) {
  const $ = _c(95);
  const {
    onOpenShop
  } = t0;
  const t1 = useAuth(_temp4);
  let t2;
  if ($[0] !== t1) {
    t2 = hasAdminPermission(t1, MANAGE);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const canManage = t2;
  const [days, setDays] = useState(30);
  let t3;
  if ($[2] !== days) {
    t3 = lastDays(days);
    $[2] = days;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const range = t3;
  const {
    data,
    isLoading,
    error
  } = useSellerClientSummary(range);
  const [editing, setEditing] = useState(null);
  const totals = data?.totals;
  const t4 = totals?.visits ?? 0;
  let t5;
  if ($[4] !== t4) {
    t5 = t4.toLocaleString("ru-RU");
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = ["\u041F\u0435\u0440\u0435\u0445\u043E\u0434\u044B \u043F\u043E \u0441\u0441\u044B\u043B\u043A\u0430\u043C", t5];
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = totals?.clients ?? 0;
  let t8;
  if ($[8] !== t7) {
    t8 = t7.toLocaleString("ru-RU");
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = ["\u041F\u0440\u0438\u0432\u044F\u0437\u0430\u043D\u043E \u043A\u043B\u0438\u0435\u043D\u0442\u043E\u0432", t8];
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = totals?.inGrace ?? 0;
  let t11;
  if ($[12] !== t10) {
    t11 = t10.toLocaleString("ru-RU");
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11) {
    t12 = ["\u0421\u0435\u0439\u0447\u0430\u0441 \u0432 \u043B\u044C\u0433\u043E\u0442\u0435", t11];
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = `${totals?.orders ?? 0} (${totals?.graceOrders ?? 0})`;
  let t14;
  if ($[16] !== t13) {
    t14 = ["\u0417\u0430\u043A\u0430\u0437\u043E\u0432 (\u043B\u044C\u0433\u043E\u0442\u043D\u044B\u0445)", t13];
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = totals?.revenue ?? 0;
  let t16;
  if ($[18] !== t15) {
    t16 = rub(t15);
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== t16) {
    t17 = ["\u0421\u0443\u043C\u043C\u0430 \u0442\u043E\u0432\u0430\u0440\u043E\u0432", t16];
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const t18 = totals?.savedCommission ?? 0;
  let t19;
  if ($[22] !== t18) {
    t19 = rub(t18);
    $[22] = t18;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== t19) {
    t20 = ["\u041A\u043E\u043C\u0438\u0441\u0441\u0438\u0438 \u043D\u0435 \u0432\u0437\u044F\u0442\u043E", t19];
    $[24] = t19;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  const t21 = totals?.acquiringRetained ?? 0;
  let t22;
  if ($[26] !== t21) {
    t22 = rub(t21);
    $[26] = t21;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== t22) {
    t23 = ["\u0423\u0434\u0435\u0440\u0436\u0430\u043D\u043E \u044D\u043A\u0432\u0430\u0439\u0440\u0438\u043D\u0433\u0430", t22];
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== t12 || $[31] !== t14 || $[32] !== t17 || $[33] !== t20 || $[34] !== t23 || $[35] !== t6 || $[36] !== t9) {
    t24 = [t6, t9, t12, t14, t17, t20, t23];
    $[30] = t12;
    $[31] = t14;
    $[32] = t17;
    $[33] = t20;
    $[34] = t23;
    $[35] = t6;
    $[36] = t9;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  const cards = t24;
  const t25 = String(days);
  let t26;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e => setDays(Number(e.target.value));
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = SUMMARY_DAYS.map(_temp5);
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  if ($[40] !== t25) {
    t28 = <Select value={t25} onChange={t26} aria-label={"\u041F\u0435\u0440\u0438\u043E\u0434"}>{t27}</Select>;
    $[40] = t25;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] !== range.from || $[43] !== range.to) {
    t29 = <span className="text-sm text-muted-foreground">{range.from} - {range.to}</span>;
    $[42] = range.from;
    $[43] = range.to;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] !== t28 || $[46] !== t29) {
    t30 = <div className="flex items-center gap-2">{t28}{t29}</div>;
    $[45] = t28;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== error) {
    t31 = error && <p className="text-sm text-destructive">{(error as Error).message}</p>;
    $[48] = error;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== cards || $[51] !== isLoading) {
    t32 = <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="sc-totals">{cards.map(t33 => {
        const [label, value] = t33;
        return <div key={label} className="rounded-lg border p-4"><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1 text-lg font-semibold tabular-nums">{isLoading ? "\u2026" : value}</div></div>;
      })}</div>;
    $[50] = cards;
    $[51] = isLoading;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  let t38;
  let t39;
  let t40;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <TableHead>Магазин</TableHead>;
    t34 = <TableHead>Код ссылки</TableHead>;
    t35 = <TableHead>Льготный срок</TableHead>;
    t36 = <TableHead className="text-right">Переходы</TableHead>;
    t37 = <TableHead className="text-right">Клиентов</TableHead>;
    t38 = <TableHead className="text-right">Заказов</TableHead>;
    t39 = <TableHead className="text-right">Сумма</TableHead>;
    t40 = <TableHead className="text-right">Не взято комиссии</TableHead>;
    $[53] = t33;
    $[54] = t34;
    $[55] = t35;
    $[56] = t36;
    $[57] = t37;
    $[58] = t38;
    $[59] = t39;
    $[60] = t40;
  } else {
    t33 = $[53];
    t34 = $[54];
    t35 = $[55];
    t36 = $[56];
    t37 = $[57];
    t38 = $[58];
    t39 = $[59];
    t40 = $[60];
  }
  let t41;
  if ($[61] !== canManage) {
    t41 = canManage && <TableHead />;
    $[61] = canManage;
    $[62] = t41;
  } else {
    t41 = $[62];
  }
  let t42;
  if ($[63] !== t41) {
    t42 = <TableHeader><TableRow>{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}</TableRow></TableHeader>;
    $[63] = t41;
    $[64] = t42;
  } else {
    t42 = $[64];
  }
  let t43;
  if ($[65] !== data?.shops || $[66] !== isLoading) {
    t43 = !isLoading && (data?.shops ?? []).length === 0 && <TableRow><TableCell colSpan={9} className="h-20 text-center text-muted-foreground">Пока ни один магазин не привёл клиентов.</TableCell></TableRow>;
    $[65] = data?.shops;
    $[66] = isLoading;
    $[67] = t43;
  } else {
    t43 = $[67];
  }
  let t44;
  if ($[68] !== data?.shops) {
    t44 = data?.shops ?? [];
    $[68] = data?.shops;
    $[69] = t44;
  } else {
    t44 = $[69];
  }
  let t45;
  if ($[70] !== canManage || $[71] !== data?.settings || $[72] !== onOpenShop || $[73] !== t44) {
    let t46;
    if ($[75] !== canManage || $[76] !== data?.settings || $[77] !== onOpenShop) {
      t46 = s_0 => <TableRow key={s_0.shopId} data-testid="sc-shop-row"><TableCell><button type="button" className="font-medium hover:underline" onClick={() => onOpenShop(s_0.shopId)}>{s_0.shopName}</button></TableCell><TableCell className="font-mono text-xs">{s_0.code ?? "-"}</TableCell><TableCell className="whitespace-nowrap">{s_0.graceMonths == null ? <span className="text-muted-foreground">общий, {monthsLabel(data?.settings.graceMonths ?? 3)}</span> : <span className="font-medium" title={s_0.graceNote}>свой, {monthsLabel(s_0.graceMonths)}</span>}</TableCell><TableCell className="text-right tabular-nums">{s_0.visits}</TableCell><TableCell className="text-right tabular-nums">{s_0.clients}<div className="text-xs text-muted-foreground">в льготе {s_0.inGrace}</div></TableCell><TableCell className="text-right tabular-nums">{s_0.orders}<div className="text-xs text-muted-foreground">льготных {s_0.graceOrders}</div></TableCell><TableCell className="text-right tabular-nums whitespace-nowrap">{rub(s_0.revenue)}</TableCell><TableCell className="text-right tabular-nums whitespace-nowrap">{rub(s_0.savedCommission)}</TableCell>{canManage && <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => setEditing(s_0)} data-testid="sc-terms">Срок</Button></TableCell>}</TableRow>;
      $[75] = canManage;
      $[76] = data?.settings;
      $[77] = onOpenShop;
      $[78] = t46;
    } else {
      t46 = $[78];
    }
    t45 = t44.map(t46);
    $[70] = canManage;
    $[71] = data?.settings;
    $[72] = onOpenShop;
    $[73] = t44;
    $[74] = t45;
  } else {
    t45 = $[74];
  }
  let t46;
  if ($[79] !== t43 || $[80] !== t45) {
    t46 = <TableBody>{t43}{t45}</TableBody>;
    $[79] = t43;
    $[80] = t45;
    $[81] = t46;
  } else {
    t46 = $[81];
  }
  let t47;
  if ($[82] !== t42 || $[83] !== t46) {
    t47 = <div className="overflow-x-auto rounded-lg border"><Table>{t42}{t46}</Table></div>;
    $[82] = t42;
    $[83] = t46;
    $[84] = t47;
  } else {
    t47 = $[84];
  }
  let t48;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="text-xs text-muted-foreground">Привязка магазина к покупателю не отменяет обычную комиссию с его покупок у других магазинов. «Не взято комиссии» - обычная ставка магазина минус удержанный эквайринг по заказам в льготный срок; логистика, возвраты и CPA удерживаются как обычно.</p>;
    $[85] = t48;
  } else {
    t48 = $[85];
  }
  let t49;
  if ($[86] !== data?.settings || $[87] !== editing) {
    t49 = editing && <TermsDialog shop={editing} defaultMonths={data?.settings.graceMonths ?? 3} onClose={() => setEditing(null)} />;
    $[86] = data?.settings;
    $[87] = editing;
    $[88] = t49;
  } else {
    t49 = $[88];
  }
  let t50;
  if ($[89] !== t30 || $[90] !== t31 || $[91] !== t32 || $[92] !== t47 || $[93] !== t49) {
    t50 = <div className="flex flex-col gap-4">{t30}{t31}{t32}{t47}{t48}{t49}</div>;
    $[89] = t30;
    $[90] = t31;
    $[91] = t32;
    $[92] = t47;
    $[93] = t49;
    $[94] = t50;
  } else {
    t50 = $[94];
  }
  return t50;
}
function _temp5(d) {
  return <option key={d} value={d}>{d} дней</option>;
}
function _temp4(s) {
  return s.user;
}
function TermsDialog({
  shop,
  defaultMonths,
  onClose
}: {
  shop: SellerClientShopSummary;
  defaultMonths: number;
  onClose: () => void;
}) {
  const {
    setTerms
  } = useSellerClientMutations();
  const [own, setOwn] = useState(shop.graceMonths != null);
  const [months, setMonths] = useState(String(shop.graceMonths ?? defaultMonths));
  const [note, setNote] = useState(shop.graceNote);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const value = Number(months);
  const valid = !own || Number.isInteger(value) && value >= 0 && value <= 36;
  const submit = async () => {
    setError("");
    try {
      await setTerms.mutateAsync({
        shopId: shop.shopId,
        input: {
          graceMonths: own ? value : null,
          note: note.trim(),
          reason: reason.trim() || undefined
        }
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return <Dialog open onOpenChange={open => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Льготный срок: {shop.shopName}</DialogTitle>
					<DialogDescription>
						Действует на заказы, оформленные после изменения: срок считается от даты привязки клиента. Уже оформленные заказы не
						пересчитываются.
					</DialogDescription>
				</DialogHeader>
				<label className="flex items-center gap-2 text-sm">
					<input type="radio" checked={!own} onChange={() => setOwn(false)} /> Общий срок ({monthsLabel(defaultMonths)})
				</label>
				<label className="flex items-center gap-2 text-sm">
					<input type="radio" checked={own} onChange={() => setOwn(true)} data-testid="sc-terms-own" /> Свой срок, месяцев:
					<Input value={months} onChange={e_0 => setMonths(e_0.target.value)} disabled={!own} inputMode="numeric" className="w-20" data-testid="sc-terms-months" />
				</label>
				{own && <Input value={note} onChange={e_1 => setNote(e_1.target.value)} placeholder="Основание: договорённость, письмо…" maxLength={500} />}
				<ReasonField value={reason} onChange={setReason} />
				{!valid && <p className="text-sm text-destructive">Срок - целое число месяцев от 0 до 36.</p>}
				{error && <p className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Отмена
					</Button>
					<Button disabled={!valid || setTerms.isPending} onClick={() => void submit()}>
						{setTerms.isPending && <Loader2 className="size-4 animate-spin" />}
						Сохранить
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}

// ── Настройки ──────────────────────────────────────────────────────────────

function SettingsTab() {
  const canEdit = hasAdminPermission(useAuth(s => s.user), MANAGE);
  const {
    data
  } = useSellerClientSettings();
  const {
    saveSettings
  } = useSellerClientMutations();
  const [draft, setDraft] = useState<{
    enabled: boolean;
    graceMonths: string;
    acquiring: string;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  useEffect(() => {
    if (data && !draft) setDraft({
      enabled: data.enabled,
      graceMonths: String(data.graceMonths),
      acquiring: String(Math.round(data.acquiringRate * 10000) / 100).replace(".", ",")
    });
  }, [data, draft]);
  if (!draft || !data) return <Loader2 className="size-4 animate-spin" />;
  const months = Number(draft.graceMonths);
  const acquiring = Number(draft.acquiring.replace(",", "."));
  const valid = Number.isInteger(months) && months >= 0 && months <= 36 && Number.isFinite(acquiring) && acquiring >= 0 && acquiring <= 20;
  const save = async () => {
    setMessage(null);
    try {
      const next = await saveSettings.mutateAsync({
        enabled: draft.enabled,
        graceMonths: months,
        acquiringRate: Math.round(acquiring * 100) / 10000,
        ...(reason.trim() ? {
          reason: reason.trim()
        } : {})
      });
      setDraft({
        enabled: next.enabled,
        graceMonths: String(next.graceMonths),
        acquiring: String(Math.round(next.acquiringRate * 10000) / 100).replace(".", ",")
      });
      setReason("");
      setMessage({
        ok: true,
        text: "Сохранено. Новые условия действуют для заказов, оформленных после изменения."
      });
    } catch (e) {
      setMessage({
        ok: false,
        text: (e as Error).message
      });
    }
  };
  return <div className="flex max-w-2xl flex-col gap-5" data-testid="sc-settings">
			{!canEdit && <p className="text-sm text-muted-foreground">Менять настройки может роль с правом «Управление промокодами».</p>}
			<label className="flex items-center gap-2 text-sm">
				<input type="checkbox" checked={draft.enabled} disabled={!canEdit} onChange={e_0 => setDraft({
        ...draft,
        enabled: e_0.target.checked
      })} data-testid="sc-enabled" />
				Льгота для клиентов продавцов включена
			</label>
			<div className="grid gap-4 sm:grid-cols-2">
				<div className="flex flex-col gap-1">
					<label htmlFor="sc-months" className="text-sm">
						Льготный срок, месяцев
					</label>
					<Input id="sc-months" value={draft.graceMonths} disabled={!canEdit} inputMode="numeric" onChange={e_1 => setDraft({
          ...draft,
          graceMonths: e_1.target.value
        })} />
					<span className="text-xs text-muted-foreground">От даты привязки покупателя. У магазина можно задать свой срок во вкладке «Магазины».</span>
				</div>
				<div className="flex flex-col gap-1">
					<label htmlFor="sc-acquiring" className="text-sm">
						Ставка эквайринга, %
					</label>
					<Input id="sc-acquiring" value={draft.acquiring} disabled={!canEdit} inputMode="decimal" onChange={e_2 => setDraft({
          ...draft,
          acquiring: e_2.target.value
        })} data-testid="sc-acquiring" />
					<span className="text-xs text-muted-foreground">
						Комиссия OSMIX по льготным заказам - 0 %, удерживается только эквайринг по этой ставке; логистика, возвраты и
						CPA - как обычно. В системе ставки эквайринга не было - укажите ставку по договору с платёжным провайдером.
						Сейчас: {pct(data.acquiringRate)}.
					</span>
				</div>
			</div>
			<ReasonField value={reason} onChange={setReason} />
			<p className="text-xs text-muted-foreground">
				Окно привязки - 30 дней после перехода по ссылке магазина на устройстве покупателя. Привязывается только новый для OSMIX покупатель (без
				оплаченных заказов) и только к одному магазину.
			</p>
			{!valid && <p className="text-sm text-destructive">Срок - целое число от 0 до 36, ставка - от 0 до 20 %.</p>}
			{message && <p className={cn("text-sm", message.ok ? "text-emerald-700" : "text-destructive")}>{message.text}</p>}
			{canEdit && <div>
					<Button onClick={() => void save()} disabled={!valid || saveSettings.isPending} data-testid="sc-save">
						{saveSettings.isPending && <Loader2 className="size-4 animate-spin" />}
						Сохранить настройки
					</Button>
				</div>}
		</div>;
}